import { useMemo, useState } from 'react';
import { Box, Container, Stack } from '@mui/material';
import { decomposeColor, recomposeColor } from '@mui/material/styles';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';
import DateTimeCell from '@/shared/components/DateTimeCell';
import AppPage from '@/shared/components/PageComponents/AppPage';
import PageHeader from '@/shared/components/PageHeader';
import StatusBadge from '@/shared/components/StatusBadge';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { cx } from '@/styles/muiTheme';

import useAuditLogEntriesQuery, { AuditLogEntry } from '../hooks/useAuditLogEntriesQuery';

const PAGE_SIZE = 100;

const statusColor = (status: number | null) => {
  if (!status) return cx.text.muted;
  if (status < 300) return cx.green;
  if (status < 500) return cx.gold;
  return cx.red.main;
};

/**
 * Pill hue per verb. Only the mutating methods carry a color — GET and anything
 * else fall through to the same muted neutral `statusColor` uses, so a scan of
 * the column lands on the requests that changed state.
 *
 * `httpMethod` is a free-form string on the entry, hence the uppercase lookup.
 */
const METHOD_COLORS: Record<string, string> = {
  POST: cx.blue.hover,
  PATCH: cx.gold,
  DELETE: cx.red.main,
};

const methodColor = (method: string) => METHOD_COLORS[method.toUpperCase()] ?? cx.text.muted;

/** Response times at or under this are pure green; at or over TIME_SLOW_MS, pure red. */
const TIME_FAST_MS = 10;
const TIME_SLOW_MS = 200;

const mixColors = (from: string, to: string, ratio: number) => {
  const a = decomposeColor(from).values;
  const b = decomposeColor(to).values;

  return recomposeColor({
    type: 'rgb',
    values: [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * ratio)) as [
      number,
      number,
      number,
    ],
  });
};

/**
 * Continuous heat over the response time, clamped to the two anchors above so
 * the extremes stay learnable colors and only the band between them moves.
 *
 * The ramp is two legs via gold rather than one green -> red leg: interpolated
 * directly, those two cross through a desaturated olive at the midpoint, which
 * is exactly where "getting slow" most needs to read.
 */
const responseTimeColor = (ms: number) => {
  const t = Math.min(Math.max((ms - TIME_FAST_MS) / (TIME_SLOW_MS - TIME_FAST_MS), 0), 1);

  return t < 0.5 ? mixColors(cx.green, cx.gold, t * 2) : mixColors(cx.gold, cx.red.main, t * 2 - 1);
};

const AuditLogsPage = () => {
  const title = 'Audit Logs';

  useDocumentTitle(title);

  const { sessionState } = useAuth();
  const isAuthReady = isSignedIn(sessionState);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);

  const entriesQuery = useAuditLogEntriesQuery({
    search,
    limit: PAGE_SIZE,
    offset: pageIndex * PAGE_SIZE,
  });

  const columns = useMemo<MRT_ColumnDef<AuditLogEntry>[]>(
    () => [
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        size: 150,
        Cell: ({ row }) => <DateTimeCell value={row.original.timestamp} />,
      },
      {
        id: 'action',
        header: 'Action',
        size: 200,
        // Only rows a handler tagged with auditAction carry one; the target id
        // rides along so the row reads as "what was done to which record".
        Cell: ({ row }) =>
          row.original.action && (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
               <Box component="span" sx={{ fontFamily: cx.fontMono, color: '#fff' }}>
                  {row.original.action}
                </Box>
     
            </Stack>
          ),
      },
      {
        accessorKey: 'httpMethod',
        header: 'Method',
        size: 100,
        Cell: ({ row }) => (
          <StatusBadge
            label={row.original.httpMethod}
            color={methodColor(row.original.httpMethod)}
          />
        ),
      },
      {
        accessorKey: 'apiEndpoint',
        header: 'Endpoint',
        size: 320,
        muiTableBodyCellProps: { sx: { fontFamily: cx.fontMono, color: cx.text.tertiary } },
      },
      {
        accessorKey: 'responseStatus',
        header: 'Status',
        size: 110,
        Cell: ({ row }) => (
          <StatusBadge
            label={String(row.original.responseStatus ?? '—')}
            color={statusColor(row.original.responseStatus)}
          />
        ),
      },
      {
        accessorKey: 'responseTime',
        header: 'Time',
        size: 100,
        Cell: ({ row }) => {
          const ms = row.original.responseTime;

          if (ms == null) return '—';

          return (
            <Box component="span" sx={{ color: responseTimeColor(ms) }}>
              {ms} ms
            </Box>
          );
        },
      },
      {
        id: 'user',
        header: 'User',
        size: 220,
        accessorFn: (row) => row.userEmail ?? (row.userId != null ? String(row.userId) : '—'),
      },
      { accessorKey: 'orgId', header: 'Org', size: 80 },
      { accessorKey: 'ipAddress', header: 'IP', size: 140 },
      {
        id: 'source',
        header: 'Source',
        size: 160,
        // The short session id is enough to tell which rows belong together;
        // the detail panel has the full one.
        Cell: ({ row }) =>
          row.original.isMcp && (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <StatusBadge label="MCP" color={cx.light[100]} />
              {row.original.mcpSessionId && (
                <Box component="span" sx={{ fontFamily: cx.fontMono, color: cx.text.tertiary }}>
                  {row.original.mcpSessionId.slice(0, 8)}
                </Box>
              )}
            </Stack>
          ),
      },
    ],
    [],
  );

  if (!isAuthReady) {
    return (
      <AppPage title={title} requiredPermissions={['menu:admin:auditlogs']}>
        <Box sx={{ padding: 4, color: cx.text.muted }}>Loading…</Box>
      </AppPage>
    );
  }

  return (
    <AppPage title={title} requiredPermissions={['menu:admin:auditlogs']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader title={title} subtitle="Recorded API requests, newest first." />

        <MaterialReactTable
          columns={columns}
          data={entriesQuery.data?.entries ?? []}
          // Both search and paging are answered by the API: a page holds at
          // most PAGE_SIZE rows, so filtering or counting locally would only
          // ever describe the page in hand.
          manualFiltering
          manualPagination
          rowCount={entriesQuery.data?.total ?? 0}
          onGlobalFilterChange={(value) => {
            setSearch(typeof value === 'string' ? value : '');
            setPageIndex(0);
          }}
          onPaginationChange={(updater) => {
            const next =
              typeof updater === 'function' ? updater({ pageIndex, pageSize: PAGE_SIZE }) : updater;
            setPageIndex(next.pageIndex);
          }}
          enableColumnFilters={false}
          enableSorting={false}
          // Hidden to start with; the show/hide columns menu brings them back.
          initialState={{
            columnVisibility: { apiEndpoint: false, orgId: false, ipAddress: false, source: false },
          }}
          muiSearchTextFieldProps={{ placeholder: 'Search endpoint, method, user or IP' }}
          // The payload is free-form JSON that would never fit a column.
          renderDetailPanel={({ row }) => (
            <Box
              component="pre"
              sx={{
                margin: 0,
                padding: 2,
                fontFamily: cx.fontMono,
                fontSize: 12,
                color: cx.text.tertiary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(
                {
                  queryParams: row.original.queryParams,
                  requestPayload: row.original.requestPayload,
                  errorMessage: row.original.errorMessage,
                  details: row.original.details ?? undefined,
                  mcpSessionId: row.original.mcpSessionId ?? undefined,
                },
                null,
                2,
              )}
            </Box>
          )}
          state={{
            globalFilter: search,
            pagination: { pageIndex, pageSize: PAGE_SIZE },
            isLoading: entriesQuery.isLoading,
            showProgressBars: entriesQuery.isFetching,
          }}
        />
      </Container>
    </AppPage>
  );
};

export default AuditLogsPage;
